import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import hasPermissions from "../../utils/hasPermissions";
import { PERMISSIONS } from "../../utils/constants";
import { useSelector } from "react-redux";
import { fetchLeagues, putLeague } from "../../api/leagues";
import ManageFieldsDialog from "../../components/customApiBlock/manageFieldsDialog";
import { fetchAllSports } from "../../api/sports";

const createColumns = (handleOpenEditDialog, sportsHash) => {
  return [
    {
      field: "id",
      headerName: "Id",
      flex: 1,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "sportId",
      headerName: "Sport",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => sportsHash.get(row.sportId)?.name ?? "—",
    },
    {
      field: "urlSegment",
      headerName: "Url segment",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            padding: '5px',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_LEAGUES)}
            onClick={() => handleOpenEditDialog(row)}
          >
            Edit
          </Button>
        </Box>
      ),
    },
  ];
};

export default function Leagues() {
  const source = useSelector((state) => state.source.currentSource);
  const [leagues, setLeagues] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentLeague, setCurrentLeague] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [sports, setSports] = useState(null);
  const [filters, setFilters] = useState({
    name: "",
    sportId: "",
  });
  const [tempFilters, setTempFilters] = useState({
    name: "",
    sportId: "",
  });

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentLeague(null);
  };

  const createParams = useCallback(() => {
    const filterParams = [];
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      filterParams.push({ key, value: filters[key] });
    });
    const pageParams = [
      { key: "take", value: pageSize },
      { key: "skip", value: page * pageSize },
    ];
    const sourceParams = [{ key: "source", value: source }];
    return [...filterParams, ...pageParams, ...sourceParams];
  }, [filters, page, pageSize, source]);

  const handleFiltersChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setTempFilters({ ...tempFilters, [name]: value });
    },
    [tempFilters]
  );

  const handleSearchClick = useCallback(() => {
    setPage(0);
    setFilters(tempFilters);
  }, [tempFilters]);

  const getLeagueList = useCallback(async () => {
    if (!source) {
      return;
    }
    const queryParams = createParams();
    const res = await fetchLeagues(queryParams);
    setLeagues(res.data);
    setTotal(res.total);
  }, [createParams, source]);

  const handleResetFilters = useCallback(async () => {
    setFilters({
      name: "",
      sportId: "",
    });
    setTempFilters({
      name: "",
      sportId: "",
    });
    setPage(0);
  }, []);

  const handleOpenEditDialog = useCallback((item) => {
    setCurrentLeague(item);
    setEditDialogOpen(true);
  }, []);

  const editFields = useMemo(
    () => [
      { name: "customName", label: "Custom name", type: "text", required: false },
    ],
    []
  );

  const handleEditConfirm = useCallback(
    async (payload) => {
      if (!currentLeague?.id) {
        return;
      }
      await putLeague(currentLeague.id, {
        customName: payload.customName ?? "",
      });
      await getLeagueList();
      setEditDialogOpen(false);
      setCurrentLeague(null);
    },
    [currentLeague, getLeagueList]
  );

  const fetchSports = useCallback(async () => {
    const sports = await fetchAllSports();
    setSports(sports);
  }, []);

  useEffect(() => {
    fetchSports();
  }, [fetchSports]);

  const sportsHash = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(sports)) {
      return map;
    }
    sports.forEach((sport) => {
      map.set(sport.id, sport);
    });
    return map;
  }, [sports]);

  const columns = useMemo(() => {
    return createColumns(handleOpenEditDialog, sportsHash);
  }, [handleOpenEditDialog, sportsHash]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
  }, []);

  useEffect(() => {
    getLeagueList();
  }, [getLeagueList]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
      <h1>Leagues</h1>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          height: "100px",
        }}
      >
        filter by
        <TextField
          name="name"
          variant="outlined"
          size="small"
          label="Name"
          value={tempFilters.name}
          onChange={(event) => handleFiltersChange(event)}
        />
        {sports && <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="leagues-sport-filter-label">Sport</InputLabel>
          <Select
            labelId="leagues-sport-filter-label"
            label="Sport"
            name="sportId"
            value={tempFilters.sportId}
            onChange={(event) => handleFiltersChange(event)}
          >
            <MenuItem value="">
              <em>All sports</em>
            </MenuItem>
            {sports.map((sport) => (
              <MenuItem key={sport.id} value={sport.id}>
                {sport.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}
        <Button variant="contained" onClick={() => handleSearchClick()}>
          Search
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleResetFilters()}
        >
          Reset
        </Button>
      </div>
      {leagues && <Box sx={{ height: "700px", width: "100%" }}>
        <DataGrid
          getRowHeight={() => 'auto'}
          rows={leagues}
          columns={columns}
          rowCount={total}
          pageSize={pageSize}
          page={page}
          paginationMode="server"
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          disableColumnFilter
          experimentalFeatures={{ newEditingApi: true }}
        />
      </Box>}
      {editDialogOpen && currentLeague && (
        <ManageFieldsDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          onConfirm={handleEditConfirm}
          data={{ ...currentLeague, customName: currentLeague.customName ?? "" }}
          fields={editFields}
          title={currentLeague.name || "Edit league"}
        />
      )}
    </div>
  );
}
